import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, RefreshCw, ShoppingCart, Mail, ArrowLeft } from "lucide-react";
import Navbar from "../components/nav/Navbar";
import SiteFooter from "../components/SiteFooter";

/* ── Error reason map ───────────────────────────────────────────────────── */
const ERROR_CONTENT = {
  checkout_failed: {
    title: "Checkout Failed",
    description: "We couldn't initiate your checkout session. This is usually a temporary issue — your cart has been saved.",
    cta: "Return to Cart",
    ctaPath: "/cart",
  },
  payment_failed: {
    title: "Payment Declined",
    description: "Your payment method was declined. Please try a different card or payment method.",
    cta: "Try Again",
    ctaPath: "/cart?checkout=1",
  },
  payment_link_conflict: {
    title: "Mixed Cart Conflict",
    description: "Some items in your cart require individual checkout via Buy Now. Please remove those items or purchase them separately.",
    cta: "Back to Cart",
    ctaPath: "/cart",
  },
  session_expired: {
    title: "Session Expired",
    description: "Your checkout session timed out. Don't worry — your cart is still saved.",
    cta: "Return to Cart",
    ctaPath: "/cart",
  },
  product_unavailable: {
    title: "Product Unavailable",
    description: "One or more items in your cart are no longer available at this price. Please review your cart.",
    cta: "Review Cart",
    ctaPath: "/cart",
  },
  unknown: {
    title: "Something Went Wrong",
    description: "An unexpected error occurred. If the problem persists, please contact our support team.",
    cta: "Back to Cart",
    ctaPath: "/cart",
  },
};

/* ══════════════════════════════════════════════════════════════════════════
   ORDER ERROR PAGE
   Route: /order/error?reason=<reason_key>
══════════════════════════════════════════════════════════════════════════ */
export default function OrderErrorPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reason = searchParams.get("reason") || "unknown";
  const content = ERROR_CONTENT[reason] || ERROR_CONTENT.unknown;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div
        style={{
          paddingTop: "var(--header-h)",
          minHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "80px 24px",
        }}
      >
        <div
          style={{
            maxWidth: 520,
            width: "100%",
            background: "#fff",
            borderRadius: 24,
            border: "1px solid hsl(var(--border))",
            padding: "48px 40px",
            textAlign: "center",
            boxShadow: "0 8px 40px rgba(0,0,0,0.06)",
          }}
        >
          {/* Icon */}
          <div
            style={{
              width: 68,
              height: 68,
              borderRadius: "50%",
              background: "#fef2f2",
              border: "1.5px solid #fca5a5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px",
            }}
          >
            <AlertTriangle size={30} style={{ color: "#dc2626" }} />
          </div>

          {/* Title */}
          <h1
            style={{
              fontFamily: "'Oswald', sans-serif",
              fontWeight: 700,
              fontSize: "clamp(1.5rem, 4vw, 2rem)",
              color: "hsl(var(--foreground))",
              lineHeight: 1.1,
              marginBottom: 12,
            }}
          >
            {content.title}
          </h1>

          {/* Description */}
          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 15,
              color: "hsl(var(--muted-foreground))",
              lineHeight: 1.65,
              marginBottom: 32,
            }}
          >
            {content.description}
          </p>

          {/* Error reference */}
          {reason !== "unknown" && (
            <div
              style={{
                background: "#f8f7ff",
                border: "1px solid hsl(var(--border))",
                borderRadius: 10,
                padding: "8px 14px",
                marginBottom: 28,
                display: "inline-block",
              }}
            >
              <span
                style={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 11,
                  color: "hsl(var(--muted-foreground))",
                  letterSpacing: "0.05em",
                }}
              >
                Error ref: {reason}
              </span>
            </div>
          )}

          {/* CTAs */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <button
              onClick={() => navigate(content.ctaPath)}
              style={{
                background: "var(--grad-primary)",
                color: "#fff",
                border: "none",
                borderRadius: 14,
                padding: "14px 0",
                fontFamily: "'Inter', sans-serif",
                fontSize: 15,
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                width: "100%",
              }}
            >
              <ShoppingCart size={15} />
              {content.cta}
            </button>

            <button
              onClick={() => navigate("/shop")}
              style={{
                background: "hsl(var(--secondary))",
                color: "hsl(var(--foreground))",
                border: "1px solid hsl(var(--border))",
                borderRadius: 14,
                padding: "12px 0",
                fontFamily: "'Inter', sans-serif",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                width: "100%",
              }}
            >
              <ArrowLeft size={14} />
              Browse Machines
            </button>

            <a
              href="mailto:support@vendbabee.com"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                padding: "11px 0",
                fontFamily: "'Inter', sans-serif",
                fontSize: 13,
                color: "hsl(var(--muted-foreground))",
                textDecoration: "none",
                borderRadius: 12,
                transition: "color 0.15s",
              }}
            >
              <Mail size={13} />
              Contact Support
            </a>
          </div>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
}
